import { effect, Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, interval, ReplaySubject, Subject } from 'rxjs';
import { FiBackendService } from '@fi-shared/fi-backend/fi-backend.service';
import { ActionType, ObjectTypes } from '@fi-shared/types';
import { FiSignalRService } from '@fi-shared/fi-signal-r/fi-signal-r.service';
import { fiDebounce, isDefined } from '@fi-shared/utils';
import { IObjectDto } from '@fi-shared/models/models';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { IItemUpdatedEventArgs, IItemUpdatedEventData } from '@fi-shared/fi-signal-r/fi-signal-r';
import { getClientGuid } from '@fi-shared/utils/client-guid';
import { MobileAppHelperBaseService } from '@fi-shared/fi-shared-services/mobile-app-helper-base.service';
import { IForm, ILightWeightFormAnswerSet } from '@fi-shared/fi-form-shared/fi-form-shared';
import { IFiChecklistModel, ISystemNotificationsCheckpointAnswer } from '@fi-shared/checklist/checklist';
import { AccessRightFilterCollectionModel } from '@fi-shared/models';
import { debounce, filter, map } from 'rxjs/operators';
import { IChatConversation } from '@fi-shared/fi-chat/fi-chat';
import { FiBackendRequestType, FiRequestsQueueService } from '@fi-shared/fi-shared-services/fi-requests-queue.service';
import { FiItemUpdatedEventType } from '@fi-shared/fi-signal-r/fi-item-update-event-type';
import { bufferDebounce } from '@fi-shared/utils/buffer-debounce';
import { IOnboardingActiveData } from '@fi-app/fi-onboarding/fi-onboarding-builder/fi-onboarding';
import { FiOnboardingStatusType } from '@fi-app/fi-onboarding/fi-onboarding-enums';
import { IUserListItemModel } from '@fi-app/user/user';

export interface IUpdateItemsSyncResult<T extends { Id?: number }> {
    deletedItemIds: number[];
    updatedItems: T[];
}

interface IInitialData {
    OnlineUserIds: number[];
    NewNewsFeeds: IObjectDto[];
    NewForumPosts: IObjectDto[];
    NewDocumentIds: number[];
    NewMediaBankFileIds: number[];
    NewFormIds: number[];
    UserAccessRequests: IUserListItemModel[];
    RemindersChecklists: IFiChecklistModel[];
    TodoForms: IForm[];
    TaskIdsToBeDone: number[];
    DraftDocumentsCount: number;
    UserAccessRights: AccessRightFilterCollectionModel;
    UnseenPersonalNotificationsCount: number;
    NewFormAnswerSets: ILightWeightFormAnswerSet[];
    NotCompletedChecklistsV2: IChecklist2ForBadge[];
    NotCompletedChecklistV2Audits: IChecklist2ForBadge[];
    CheckPointAnswerNotifications: ISystemNotificationsCheckpointAnswer[];
    Conversations: IChatConversation[];
    OnboardingActiveData: IOnboardingActiveData;
}

export interface IChecklist2ForBadge {
    ChecklistToScheduleId: number;
    BusinessUnitId: number;
    DueDateTime: string; //ISO 8601 date
}

// todo rename to InitialDataService,  load data on user change
@Injectable()
export class FiSignalRHelperService {
    private _newDocumentIds$ = new BehaviorSubject<number[]>([]);
    get newDocumentIds$() {
        return this._newDocumentIds$.asObservable();
    }

    getNewDocumentIdsCount() {
        return this._newDocumentIds$.value.length;
    }

    clearNewDocumentIds() {
        this._newDocumentIds$.next([]);
    }

    private _newMediaFileIds$ = new BehaviorSubject<number[]>([]);
    get newMediaFileIds$() {
        return this._newMediaFileIds$.asObservable();
    }

    getNewMediaFileIdsCount() {
        return this._newMediaFileIds$.value.length;
    }

    clearNewMediaFileIds() {
        this._newMediaFileIds$.next([]);
    }

    private _notCompletedChecklistsV2$ = new BehaviorSubject<IChecklist2ForBadge[]>([]);
    get notCompletedChecklistsV2$() {
        return this._notCompletedChecklistsV2$.asObservable();
    }

    private _notCompletedChecklistV2Audits$ = new BehaviorSubject<IChecklist2ForBadge[]>([]);
    get notCompletedChecklistV2Audits$() {
        return this._notCompletedChecklistV2Audits$.asObservable();
    }

    newFormIds$ = new BehaviorSubject<number[]>([]);
    newForumPostIds$ = new BehaviorSubject<number[]>([]);

    private _initialUserAccessRequests$ = new ReplaySubject<IUserListItemModel[]>(1);

    get initialUserAccessRequests() {
        return this._initialUserAccessRequests$.asObservable();
    }

    private _initialRemindersChecklists$ = new ReplaySubject<IFiChecklistModel[]>(1);

    get initialRemindersChecklists() {
        return this._initialRemindersChecklists$.asObservable();
    }

    private _initialTodoForms$ = new ReplaySubject<IForm[]>(1);
    get initialTodoForms$() {
        return this._initialTodoForms$.asObservable();
    }

    private _initialTaskIdsToBeDone$ = new ReplaySubject<number[]>(1);
    get initialTaskIdsToBeDone$() {
        return this._initialTaskIdsToBeDone$.asObservable();
    }

    private _initialDraftDocumentsCount$ = new ReplaySubject<number>(1);

    get initialDraftDocumentsCount() {
        return this._initialDraftDocumentsCount$.asObservable();
    }

    private _initialUserAccessRights$ = new ReplaySubject<AccessRightFilterCollectionModel>(1);

    get initialUserAccessRights() {
        return this._initialUserAccessRights$.asObservable();
    }

    private _initialUnseenPersonalNotificationsCount$ = new ReplaySubject<number>(1);

    get initialUnseenPersonalNotificationsCount() {
        return this._initialUnseenPersonalNotificationsCount$.asObservable();
    }

    private _initialNewFormAnswerSets$ = new ReplaySubject<ILightWeightFormAnswerSet[]>(1);

    get initialNewFormAnswerSets() {
        return this._initialNewFormAnswerSets$.asObservable();
    }

    private _initialCheckPointAnswerNotifications$ = new ReplaySubject<ISystemNotificationsCheckpointAnswer[]>(1);

    get initialCheckPointAnswerNotifications() {
        return this._initialCheckPointAnswerNotifications$.asObservable();
    }

    private _initialConversations$ = new ReplaySubject<IChatConversation[]>(1);
    get initialConversations$() {
        return this._initialConversations$.asObservable();
    }

    private _newNewsFeeds$ = new BehaviorSubject<IObjectDto[]>([]);
    get newNewsFeeds$() {
        return this._newNewsFeeds$.asObservable();
    }

    private _onboardingActiveData$ = new BehaviorSubject<IOnboardingActiveData>(undefined);
    get onboardingActiveData$() {
        return this._onboardingActiveData$.asObservable();
    }

    private _activeOnboardingsCount$ = new BehaviorSubject<number>(0);
    get activeOnboardingsCount$() {
        return this._activeOnboardingsCount$.asObservable();
    }
    setActiveOnboardingsCount(onboardingActiveData: IOnboardingActiveData) {
        this._activeOnboardingsCount$.next(
            onboardingActiveData.ActiveOnboardings.filter(
                (activeOnboarding) => activeOnboarding.Status !== FiOnboardingStatusType.Completed,
            ).length,
        );
    }

    private _onlineUserIds$ = new BehaviorSubject<number[]>([]);
    get onlineUserIds$() {
        return this._onlineUserIds$.asObservable();
    }

    isUserOnline(userId: number) {
        return this._onlineUserIds$.value.indexOf(userId) !== -1;
    }

    showForumActivityInNewsFeed: boolean = false;

    private _itemUpdated$ = new Subject<IItemUpdatedEventData>();
    get itemUpdated$() {
        return this._itemUpdated$.asObservable();
    }

    constructor(
        private fiBackend: FiBackendService,
        private fiSignalRService: FiSignalRService,
        private fiUserDataService: FiUserDataService,
        private mobileAppHelper: MobileAppHelperBaseService,
        private fiRequestsQueueService: FiRequestsQueueService,
        private zone: NgZone,
    ) {
        this.fiUserDataService.onUserDataChanged$.pipe(debounce(() => interval(1000))).subscribe((userData) => {
            if (userData) {
                this.init();
            } else {
                // todo reset all stored info?
                this.fiSignalRService.closeConnection();
            }
        });

        this.fiUserDataService.onPreferredBusinessUnitChanged$.subscribe(() => {
            this.loadInitialData();
        });

        this.fiSignalRService.onConnectionStarted$.subscribe(() => {
            this.fiSignalRService.dbOperationEvent.subscribe((data) => {
                if (data.ClientGuid !== getClientGuid()) {
                    // ignore db operations from same browser tab
                    this._itemUpdated$.next(data);
                }
            });
        });

        this.fiSignalRService.userOnlineUpdatedEvent.subscribe((data) => {
            const onlineUserIds = this._onlineUserIds$.value;
            const currentUserIdx = onlineUserIds.indexOf(data.UserId);
            if (data.IsOnline && currentUserIdx === -1) {
                // somebody has been online
                onlineUserIds.push(data.UserId);
                this._onlineUserIds$.next(onlineUserIds);
            } else if (!data.IsOnline && currentUserIdx !== -1) {
                onlineUserIds.splice(currentUserIdx, 1);
                this._onlineUserIds$.next(onlineUserIds);
            }
        });
    }

    private init() {
        this.loadInitialData();

        this.fiSignalRService.itemUpdatedEvent.subscribe((data) => {
            if (data.EventType == FiItemUpdatedEventType.Module) {
                if (data.ObjectType == ObjectTypes.Document) {
                    this.processNewIds(this._newDocumentIds$, [data.ObjectId], data.ActionType);
                } else if (data.ObjectType == ObjectTypes.MediaBank) {
                    this.processNewIds(this._newMediaFileIds$, [data.ObjectId], data.ActionType);
                } else if (data.ObjectType === ObjectTypes.Form) {
                    this.processNewIds(this.newFormIds$, [data.ObjectId], data.ActionType);
                }
            }
        });

        this.fiSignalRService.itemUpdatedEvent.subscribe((data) => {
            const targetAction =
                data.EventByUserId !== this.fiUserDataService.getUserId() && data.ActionType !== ActionType.Delete
                    ? ActionType.Create
                    : ActionType.Delete;

            if (data.EventType === FiItemUpdatedEventType.Feed && data.ObjectType === ObjectTypes.ForumPost) {
                this.processNewIds(this.newForumPostIds$, [data.ObjectId], targetAction);
            } else {
                this.processNewObjectDtos(this._newNewsFeeds$, data, targetAction);
            }
        });
    }

    private loadInitialData() {
        this.fiRequestsQueueService.addRequestToQueue(() => {
            const promise = this.fiBackend
                .fetchData<IInitialData>(`NewsFeed/NewsFeed/GetInitialData?mode=${this.mobileAppHelper.frontEndMode}`, {
                    supressErrorMessage: true,
                })
                .then((data) => {
                    this._onlineUserIds$.next(data.OnlineUserIds);
                    this._newNewsFeeds$.next(data.NewNewsFeeds);
                    this.newForumPostIds$.next(data.NewForumPosts.map((item) => item.ObjectId));
                    this._newDocumentIds$.next(data.NewDocumentIds);
                    this._newMediaFileIds$.next(data.NewMediaBankFileIds);
                    this._notCompletedChecklistsV2$.next(data.NotCompletedChecklistsV2);
                    this._notCompletedChecklistV2Audits$.next(data.NotCompletedChecklistV2Audits);

                    this.newFormIds$.next(data.NewFormIds);

                    this._initialUserAccessRequests$.next(data.UserAccessRequests);
                    this._initialRemindersChecklists$.next(data.RemindersChecklists);
                    this._initialTodoForms$.next(data.TodoForms);
                    this._initialTaskIdsToBeDone$.next(data.TaskIdsToBeDone);
                    this._initialDraftDocumentsCount$.next(data.DraftDocumentsCount);
                    this._initialUserAccessRights$.next(data.UserAccessRights);
                    this._initialUnseenPersonalNotificationsCount$.next(data.UnseenPersonalNotificationsCount);
                    this._initialNewFormAnswerSets$.next(data.NewFormAnswerSets);
                    this._initialConversations$.next(data.Conversations);
                    this._initialCheckPointAnswerNotifications$.next(data.CheckPointAnswerNotifications);
                    this._onboardingActiveData$.next(data.OnboardingActiveData);
                    this.setActiveOnboardingsCount(data.OnboardingActiveData);
                });
            return promise;
        }, FiBackendRequestType.InitialData);
    }

    private processNewIds(ids$: BehaviorSubject<number[]>, eventIds: number[], actionType: ActionType) {
        if (actionType === ActionType.Delete) {
            const ids = ids$.value.filter((id) => eventIds.indexOf(id) === -1);
            ids$.next(ids);
        } else if (actionType === ActionType.Create) {
            const ids = ids$.value;
            eventIds.filter((eventId) => ids.indexOf(eventId) === -1).forEach((eventId) => ids.push(eventId));
            ids$.next(ids);
        }
    }

    private processNewObjectDtos(
        objectDtos$: BehaviorSubject<IObjectDto[]>,
        newObjectDto: IObjectDto,
        actionType: ActionType,
    ) {
        if (actionType === ActionType.Delete) {
            const objectDtos = objectDtos$.value.filter(
                (item) => !(item.ObjectId === newObjectDto.ObjectId && item.ObjectType === newObjectDto.ObjectType),
            );
            objectDtos$.next(objectDtos);
        } else if (actionType === ActionType.Create) {
            const objectDtos = objectDtos$.value;
            if (
                !objectDtos.find(
                    (item) => item.ObjectId === newObjectDto.ObjectId && item.ObjectType === newObjectDto.ObjectType,
                )
            ) {
                objectDtos.push(newObjectDto);
                objectDtos$.next(objectDtos);
            }
        }
    }

    onMarkNewsFeedAsVisited(item: IObjectDto) {
        if (item.ObjectType === ObjectTypes.ForumPost) {
            // todo new forum posts count not updated without ngZone, why?
            this.zone.run(() => this.processNewIds(this.newForumPostIds$, [item.ObjectId], ActionType.Delete));
        }
        this.zone.run(() => this.processNewObjectDtos(this._newNewsFeeds$, item, ActionType.Delete));
    }

    onMarkForumPostsAsVisited(forumPostIds: number[]) {
        this.zone.run(() => this.processNewIds(this.newForumPostIds$, forumPostIds, ActionType.Delete));
    }

    onMarkForumPostsAsRead(forumPostIds: number[]) {
        this.onMarkForumPostsAsVisited(forumPostIds);
    }

    clearNewNewsFeeds() {
        this._newNewsFeeds$.next([]);
    }

    getNewNewsFeedsCount() {
        return this._newNewsFeeds$.value.length;
    }

    raiseUpdateItemsEvent(objectId: number, objectType: ObjectTypes, action: ActionType) {
        this._itemUpdated$.next({
            Id: objectId,
            ObjectType: objectType,
            Action: action,
        });
    }

    /**
     *
     * @param objectType
     * @param getItemsFn - should return array of existing items
     * @param loadItemsFromBackendFn - should load items from backend by given ids
     * @param updateItemsFn - contains array of updated items ()
     */
    startUpdateItemsEventProcessing<T extends { Id?: number }>(
        objectType: ObjectTypes,
        getItemsFn: () => T[],
        loadItemsFromBackendFn: (itemIds: number[]) => Promise<T[]>,
        updateItemsFn: (updatedItems: T[]) => void,
        delay?: number,
    ) {
        const itemUpdatedData: IItemUpdatedEventData[] = [];
        let processDbOperationsDebounced = fiDebounce(
            () => {
                this.processDbOperations(itemUpdatedData, getItemsFn, loadItemsFromBackendFn, updateItemsFn);
                itemUpdatedData.length = 0;
            },
            isDefined(delay) ? delay : 2000,
        );

        this._itemUpdated$.subscribe((data) => {
            if (data.ObjectType === objectType) {
                itemUpdatedData.push(data);
                processDbOperationsDebounced();
            }
        });
    }

    private processDbOperations<T extends { Id?: number }>(
        itemUpdatedData: IItemUpdatedEventData[],
        getItemsFn: () => T[],
        loadItemsFromBackendFn: (itemIds: number[]) => Promise<T[]>,
        updateItemsFn: (updatedItems: T[]) => void,
    ) {
        let items = getItemsFn().slice();
        const itemIds = items.map((item) => item.Id);

        const itemsIdsToRemove = itemUpdatedData
            .filter(
                (data) =>
                    (data.Action === ActionType.MoveToTrash || data.Action === ActionType.Delete) &&
                    itemIds.indexOf(data.Id) !== -1,
            )
            .map((data) => data.Id);
        const itemsIdsToUpdate = itemUpdatedData
            .filter(
                (data) =>
                    data.Action === ActionType.Create ||
                    data.Action === ActionType.Update ||
                    data.Action === ActionType.Restore,
            )
            .map((data) => data.Id);

        if (itemsIdsToRemove.length || itemsIdsToUpdate.length) {
            new Promise<void>((resolve, reject) => {
                // remove deleted items
                items = items.filter((item) => itemsIdsToRemove.indexOf(item.Id) === -1);

                if (!itemsIdsToUpdate.length) {
                    // no items to load from backend, resolve immediately
                    resolve();
                } else {
                    // load updated items from backend
                    loadItemsFromBackendFn(itemsIdsToUpdate).then((updatedItems) => {
                        // delete previous items
                        itemsIdsToUpdate.forEach((itemIdToUpdate) => {
                            const updatedItem = updatedItems.find((updatedItem) => updatedItem.Id === itemIdToUpdate);
                            const existingItemIndex = items.findIndex((item) => item.Id === itemIdToUpdate);
                            if (!updatedItem) {
                                // item not exists on backend, delete it
                                if (existingItemIndex !== -1) {
                                    items.splice(existingItemIndex, 1);
                                }
                            } else {
                                if (existingItemIndex === -1) {
                                    // add item from backend to existing items
                                    items.unshift(updatedItem);
                                } else {
                                    const existingItem = items[existingItemIndex];

                                    // replace existing item with item from backend
                                    for (let prop in updatedItem) {
                                        existingItem[prop] = updatedItem[prop];
                                    }
                                }
                            }
                        });

                        resolve();
                    }, reject);
                }
            }).then(() => updateItemsFn(items));
        }
    }

    /* Gathers updated item ids and after debounceMs time fetches from backend updated items
     *
     * @param args
     */
    subscribeToItemUpdated<T extends { Id?: number }>(args: {
        objectType: ObjectTypes;
        fetchItemsFn: (ids: number[]) => Promise<T[]>;
        callback: (result: IUpdateItemsSyncResult<T>) => void;
        debounceMs?: number;
        filterItemUpdatedFn?: (data: IItemUpdatedEventArgs) => boolean;
        filterDbOperationFn?: (data: IItemUpdatedEventData) => boolean;
    }) {
        if (!args.filterItemUpdatedFn) {
            args.filterItemUpdatedFn = (data) =>
                data.ActionType === ActionType.Create ||
                ((data.ActionType === ActionType.Update || data.ActionType === ActionType.Delete) &&
                    data.EventByClientGuid !== getClientGuid());
        }

        if (!args.filterDbOperationFn) {
            args.filterDbOperationFn = (data) =>
                data.Action === ActionType.Create ||
                ((data.Action === ActionType.Update || data.Action === ActionType.Delete) &&
                    data.ClientGuid !== getClientGuid());
        }

        const itemUpdated$ = new Subject<number>();

        itemUpdated$
            .pipe(
                bufferDebounce(args.debounceMs ?? 5000),
                filter((ids) => !!ids.length),
                map((ids) => [...new Set(ids)]),
            ) // receive unique ids
            .subscribe((updatedItemIds) => {
                args.fetchItemsFn(updatedItemIds).then((items) => {
                    const existingItemIds = items.map((item) => item.Id);
                    const deletedItemIds = updatedItemIds.filter((id) => !existingItemIds.includes(id));
                    args.callback({
                        deletedItemIds: deletedItemIds,
                        updatedItems: items,
                    });
                });
            });

        this.fiSignalRService.itemUpdatedEvent.subscribe((data) => {
            if (data.ObjectType === args.objectType && args.filterItemUpdatedFn(data)) {
                itemUpdated$.next(data.ObjectId);
            }
        });
        this.fiSignalRService.dbOperationEvent.subscribe((data) => {
            if (data.ObjectType === args.objectType && args.filterDbOperationFn(data)) {
                if (data.Action === ActionType.Delete) {
                    // item deleted, no need to fetch data from backend
                    args.callback({
                        deletedItemIds: [data.Id],
                        updatedItems: [],
                    });
                } else {
                    itemUpdated$.next(data.Id);
                }
            }
        });
    }
}
